import {
  getSegmentVisualStyle,
  updateSegmentVisualStyle,
} from "../landingStudio/segmentVisualStyle";
import { normalizeCustomSegmentStyle } from "../landingStudio/customVisualOwnership";

export const DEFAULT_LANDING_LAYOUT_TYPES = Object.freeze([
  "navbar",
  "hero",
  "spotlight",
  "featured",
  "brands",
  "categories",
  "about",
  "highlights",
  "contact",
  "footer",
]);

export const LANDING_LAYOUT_TYPES = Object.freeze([
  "navbar",
  "hero",
  "spotlight",
  "featured",
  "brands",
  "categories",
  "about",
  "highlights",
  "imageCarousel",
  "testimonials",
  "contact",
  "footer",
]);

export const STRUCTURAL_LANDING_TYPES = Object.freeze(["navbar", "footer"]);
const STRUCTURAL = new Set(STRUCTURAL_LANDING_TYPES);
export const CUSTOM_LANDING_TYPE = "custom";
const KNOWN = new Set([...LANDING_LAYOUT_TYPES, CUSTOM_LANDING_TYPE]);
export const LANDING_LAYOUT_MAX_SEGMENTS = 40;
const MAX_HOME_SEGMENTS = LANDING_LAYOUT_MAX_SEGMENTS - STRUCTURAL_LANDING_TYPES.length;

const VISIBILITY_PATHS = Object.freeze({
  hero: "sections.showHero",
  spotlight: "sections.showSpotlight",
  featured: "sections.showFeatured",
  brands: "sections.showBrands",
  categories: "sections.showCategories",
  about: "sections.showAbout",
  highlights: "sections.showHighlights",
  imageCarousel: "sections.showImageCarousel",
  testimonials: "sections.showTestimonials",
  contact: "sections.showContact",
});

const DATA_PATHS = Object.freeze({
  hero: ["hero"],
  spotlight: ["catalog.spotlight"],
  featured: ["catalog.featured"],
  brands: ["catalog.brands"],
  categories: ["catalog.categories"],
  about: ["about"],
  highlights: ["highlightsIntro", "highlights"],
  imageCarousel: ["imageCarousel"],
  testimonials: ["testimonials"],
  contact: ["contact"],
});

function clone(value) {
  if (value === undefined) return undefined;
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function getAtPath(source, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => current?.[key], source);
}

function setAtPath(source, path, value) {
  const parts = String(path || "").split(".").filter(Boolean);
  if (!parts.length) return source;
  let cursor = source;
  parts.forEach((key, index) => {
    if (index === parts.length - 1) {
      cursor[key] = clone(value);
      return;
    }
    if (!cursor[key] || typeof cursor[key] !== "object" || Array.isArray(cursor[key])) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  });
  return source;
}

function deepMerge(base, source) {
  if (source === undefined) return clone(base);
  if (!source || typeof source !== "object" || Array.isArray(source)) return clone(source);
  const output = base && typeof base === "object" && !Array.isArray(base) ? clone(base) : {};
  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = deepMerge(output[key], value);
    } else {
      output[key] = clone(value);
    }
  });
  return output;
}

function cleanId(value, fallback) {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+/, "")
    .replace(/-+$/g, "")
    .slice(0, 80);
  return cleaned || fallback;
}

function uniqueId(base, used) {
  const normalized = cleanId(base, "section");
  let candidate = normalized;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${normalized.slice(0, 72)}-${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
}

export function defaultLandingLayout() {
  return {
    segments: DEFAULT_LANDING_LAYOUT_TYPES.map((type) => ({ id: type, type })),
  };
}

export function normalizeLandingLayout(layout) {
  const raw = Array.isArray(layout)
    ? layout
    : Array.isArray(layout?.segments)
      ? layout.segments
      : null;
  if (!raw) return defaultLandingLayout();

  const structuralSource = Object.fromEntries(
    raw
      .map((item) => (typeof item === "string" ? { id: item, type: item } : (item || {})))
      .filter((item) => STRUCTURAL.has(String(item?.type || item?.id || "").trim()))
      .map((item) => {
        const type = String(item?.type || item?.id || "").trim();
        return [type, item];
      })
  );
  const used = new Set(LANDING_LAYOUT_TYPES);
  const canonicalSeen = new Set();
  const home = [];

  raw.slice(0, LANDING_LAYOUT_MAX_SEGMENTS).forEach((item) => {
    if (home.length >= MAX_HOME_SEGMENTS) return;
    const type = String(typeof item === "string" ? item : item?.type || item?.segmentType || "").trim();
    if (!KNOWN.has(type) || STRUCTURAL.has(type)) return;

    const fallbackId = type === CUSTOM_LANDING_TYPE ? `seccion-${home.length + 1}` : type;
    const rawRequestedId = String(typeof item === "string" ? type : item?.id || fallbackId).trim();
    const canonicalRequest = type !== CUSTOM_LANDING_TYPE &&
      rawRequestedId.toLowerCase() === String(type).toLowerCase();
    const requestedId = canonicalRequest ? type : cleanId(rawRequestedId, fallbackId);
    let id;
    if (canonicalRequest && !canonicalSeen.has(type)) {
      canonicalSeen.add(type);
      id = type;
    } else {
      id = uniqueId(requestedId, used);
    }
    const segment = { id, type };
    if (item && typeof item === "object" && item.anchor) segment.anchor = cleanId(item.anchor, id);
    if (item && typeof item === "object" && item.visible === false) segment.visible = false;
    if (item && typeof item === "object" && item.visualStyle && typeof item.visualStyle === "object") {
      segment.visualStyle = clone(item.visualStyle);
    }
    if (item && typeof item === "object" && item.style && typeof item.style === "object") {
      segment.style = clone(item.style);
    }
    if ((id !== type || type === CUSTOM_LANDING_TYPE) && item && typeof item === "object") {
      if (item.data && typeof item.data === "object") segment.data = clone(item.data);
    }
    home.push(segment);
  });

  const structuralSegment = (type) => {
    const rawSegment = structuralSource[type];
    return {
      id: type,
      type,
      ...(rawSegment?.visible === false ? { visible: false } : {}),
      ...(rawSegment?.visualStyle && typeof rawSegment.visualStyle === "object"
        ? { visualStyle: clone(rawSegment.visualStyle) }
        : {}),
    };
  };

  return {
    segments: [
      structuralSegment("navbar"),
      ...home,
      structuralSegment("footer"),
    ],
  };
}

export function getLandingSegments(form) {
  return normalizeLandingLayout(form?.layout).segments;
}

export function isStructuralLandingType(type) {
  return STRUCTURAL.has(type);
}

export function canDuplicateLandingType(type) {
  return KNOWN.has(type) && !STRUCTURAL.has(type);
}

export function snapshotLandingSegment(form, type) {
  const data = {};
  (DATA_PATHS[type] || []).forEach((path) => {
    const value = getAtPath(form, path);
    if (value !== undefined) setAtPath(data, path, value);
  });
  return {
    data,
    ...(type === CUSTOM_LANDING_TYPE
      ? { style: clone(normalizeCustomSegmentStyle()) }
      : { visualStyle: clone(getSegmentVisualStyle(form?.theme, type)) }),
  };
}

export function createDuplicateLandingSegment(form, sourceSegment) {
  const type = String(sourceSegment?.type || "");
  if (!canDuplicateLandingType(type)) return null;

  const baseSnapshot = sourceSegment?.id !== type && sourceSegment?.data
    ? {
        data: clone(sourceSegment.data),
        ...(type === CUSTOM_LANDING_TYPE
          ? { style: clone(normalizeCustomSegmentStyle(sourceSegment.style)) }
          : { visualStyle: clone(sourceSegment.visualStyle || getSegmentVisualStyle(form?.theme, type)) }),
      }
    : snapshotLandingSegment(form, type);

  const current = getLandingSegments(form);
  if (current.length >= LANDING_LAYOUT_MAX_SEGMENTS) return null;
  const used = new Set([...LANDING_LAYOUT_TYPES, ...current.map((segment) => segment.id)]);
  let rawId = "";
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    rawId = `${type}-${crypto.randomUUID().slice(0, 8)}`;
  } else {
    rawId = `${type}-${Date.now().toString(36)}`;
  }

  return {
    id: uniqueId(rawId, used),
    type,
    data: clone(baseSnapshot.data),
    ...(baseSnapshot.visualStyle ? { visualStyle: clone(baseSnapshot.visualStyle) } : {}),
    ...(baseSnapshot.style ? { style: clone(baseSnapshot.style) } : {}),
  };
}

export function landingSegmentAnchor(segment) {
  if (!segment) return "";
  const explicit = String(segment.anchor || "").trim();
  if (explicit) return `#${cleanId(explicit, "section")}`;
  const id = String(segment.id || segment.type || "").trim();
  const type = String(segment.type || "").trim();
  if (!id) return "";
  if (type === CUSTOM_LANDING_TYPE) return `#${cleanId(id, "section")}`;
  if (id === type) return `#${cleanId(type, "section")}`;
  return `#section-${cleanId(id, "section")}`;
}

export function createCustomLandingSegment(layout) {
  const current = normalizeLandingLayout(layout);
  if (current.segments.length >= LANDING_LAYOUT_MAX_SEGMENTS) return null;
  const used = new Set([...LANDING_LAYOUT_TYPES, CUSTOM_LANDING_TYPE, ...current.segments.map((segment) => segment.id)]);
  const suffix = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().slice(0, 8)
    : Date.now().toString(36);
  const id = uniqueId(`seccion-${suffix}`, used);
  return {
    id,
    type: CUSTOM_LANDING_TYPE,
    anchor: id,
    visible: true,
    data: {
      custom: {
        eyebrow: "",
        title: "Nueva sección",
        description: "",
        imageUrl: "",
        imageAlt: "",
        imagePosition: "center",
        layout: "image-right",
        youtubeUrl: "",
        calendlyUrl: "",
        ctaLabel: "",
        ctaUrl: "",
      },
    },
    style: clone(normalizeCustomSegmentStyle()),
  };
}

export function appendLandingSegment(layout, segment) {
  if (!segment) return normalizeLandingLayout(layout);
  const normalized = normalizeLandingLayout(layout);
  if (normalized.segments.length >= LANDING_LAYOUT_MAX_SEGMENTS) return normalized;
  const segments = [...normalized.segments];
  segments.splice(Math.max(1, segments.length - 1), 0, clone(segment));
  return normalizeLandingLayout({ segments });
}

export function updateLandingSegment(layout, segmentId, patch = {}) {
  const normalized = normalizeLandingLayout(layout);
  return {
    segments: normalized.segments.map((segment) => {
      if (segment.id !== segmentId || STRUCTURAL.has(segment.type)) return segment;
      const next = { ...segment, ...clone(patch), id: segment.id, type: segment.type };
      if (Object.prototype.hasOwnProperty.call(patch, "anchor")) {
        const anchor = cleanId(patch.anchor, "");
        if (anchor) next.anchor = anchor;
        else delete next.anchor;
      }
      return next;
    }),
  };
}

export function applyLandingSegmentSnapshot(form, segment) {
  if (!segment || segment.id === segment.type || !segment.data) return form;
  const next = deepMerge(form, segment.data);
  if (segment.visualStyle && next?.theme) {
    next.theme.template = updateSegmentVisualStyle(
      next.theme.template,
      segment.type,
      segment.visualStyle
    );
  }
  const visibilityPath = VISIBILITY_PATHS[segment.type];
  if (visibilityPath) setAtPath(next, visibilityPath, true);
  if (["spotlight", "featured", "brands", "categories"].includes(segment.type)) {
    setAtPath(next, "sections.showProducts", true);
  }
  return next;
}

export function moveLandingSegment(layout, segmentId, direction) {
  const normalized = normalizeLandingLayout(layout);
  const segments = [...normalized.segments];
  const index = segments.findIndex((segment) => segment.id === segmentId);
  if (index < 0 || STRUCTURAL.has(segments[index]?.type)) return normalized;

  const step = direction < 0 ? -1 : 1;
  let target = index + step;
  while (target >= 0 && target < segments.length && STRUCTURAL.has(segments[target]?.type)) {
    target += step;
  }
  if (target < 1 || target >= segments.length - 1) return normalized;
  [segments[index], segments[target]] = [segments[target], segments[index]];
  return { segments };
}

export function removeLandingSegment(layout, segmentId) {
  const normalized = normalizeLandingLayout(layout);
  const target = normalized.segments.find((segment) => segment.id === segmentId);
  if (!target || STRUCTURAL.has(target.type)) return normalized;
  return { segments: normalized.segments.filter((segment) => segment.id !== segmentId) };
}

export function addLandingBaseSegment(layout, type) {
  const normalized = normalizeLandingLayout(layout);
  if (!KNOWN.has(type) || STRUCTURAL.has(type)) return normalized;
  if (normalized.segments.length >= LANDING_LAYOUT_MAX_SEGMENTS) return normalized;
  if (normalized.segments.some((segment) => segment.id === type)) return normalized;

  const segments = [...normalized.segments];
  const defaultIndex = LANDING_LAYOUT_TYPES.indexOf(type);
  let insertionIndex = segments.length - 1;
  for (let index = 1; index < segments.length - 1; index += 1) {
    const candidateDefaultIndex = LANDING_LAYOUT_TYPES.indexOf(segments[index].type);
    if (candidateDefaultIndex > defaultIndex) {
      insertionIndex = index;
      break;
    }
  }
  segments.splice(insertionIndex, 0, { id: type, type });
  return { segments };
}
